import React, { useRef } from 'react';

import { v4 as uuid } from 'uuid';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useTriggerIssueViewModal } from '@atlassian/jira-open-issue-view-modal-controller/src/index.tsx';
import { useDescriptionField } from '@atlassian/jira-issue-field-rich-text/src/services/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { getADFCharacterCount } from '@atlassian/jira-issue-view-common/src/utils/adf-utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useGlobalRefStoreActions } from '@atlassian/jira-refs-store/src/index.tsx';
import SquareAddIconButton from '../../../../button/square-add-icon-button.tsx';
import type { AddChildProps } from './types.tsx';
import messages from './messages.tsx';

const AddChildNew = ({ isClassicSubtaskPanel = false, onClick }: AddChildProps) => {
	const issueKey = useIssueKey();
	const [{ value }] = useDescriptionField({ issueKey });
	const { formatMessage } = useIntl();
	const label = isClassicSubtaskPanel ? messages.createSubtaskLabel : messages.createChildLabel;
	const [, { setIssueCreateSessionId }] = useTriggerIssueViewModal();
	const [, { register }] = useGlobalRefStoreActions();
	const addChildRef = useRef(null);

	const handleOnClick = (
		e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	): void => {
		const sessionId = uuid();
		setIssueCreateSessionId(sessionId);
		const hasDescription = value != null && JSON.stringify(value) !== JSON.stringify(getEmptyADF());
		register('quick-add-items.add-child-subtask', addChildRef.current);
		if (fg('add_description_range_to_add_child_click')) {
			const descriptionLength = value ? getADFCharacterCount(value) : 0;
			onClick(e, analyticsEvent, sessionId, hasDescription, descriptionLength);
			return;
		}
		onClick(e, analyticsEvent, sessionId, hasDescription);
	};

	return (
		<ErrorBoundary prefixOverride="issue" id="Safe(AddChild)">
			<Box xcss={buttonWrapperStyles}>
				<SquareAddIconButton
					onClick={handleOnClick}
					label={formatMessage(label)}
					ref={addChildRef}
				/>
			</Box>
		</ErrorBoundary>
	);
};

export const AddChild = AddChildNew;

const buttonWrapperStyles = xcss({
	marginLeft: 'space.075',
});
